import type { OpenSource } from '@parcel/analytics';
import { capture } from '@parcel/analytics/browser';
import { AppLogo } from '@parcel/ui/app-logo';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { Building2, Link as LinkIcon, Tag } from 'lucide-react';
import * as React from 'react';
import { accountWebsite } from '../../lib/account-website.js';
import {
  type ApiAccountDetail,
  type ApiLinkedProject,
  apiAccountToRow,
  useAccountDetail,
  useAccountPortfolio,
  useAccountProjects,
} from '../../lib/accounts.js';
import { useCaptureOnce } from '../../lib/analytics.js';
import { type ApiContactSummary, useAccountContacts } from '../../lib/contacts.js';
import { contactsCountShown } from '../../lib/enrich.js';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { buildReportContext } from '../../lib/feedback.js';
import { logoDomain, logoUrl } from '../../lib/logo.js';
import type { AccountRow } from '../../lib/mock-accounts.js';
import { type ProjectRow, UNKNOWN_PROJECT_TYPE, stageMeta } from '../../lib/projects-display.js';
import { formatSignalCalendarDate } from '../../lib/signal-dates.js';
import { useAccountSignals, useSignalScope } from '../../lib/signals.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import { useTheme } from '../../lib/theme.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { EnrichScopeProvider } from '../enrich/enrich-scope.js';
import { CopyButton } from '../entity-drawer/copy-button.js';
import { ListSkeleton, SectionSkeleton, TextSkeleton } from '../entity-drawer/detail-skeletons.js';
import {
  EntityDetailLayout,
  type RecordDetailChromeProps,
} from '../entity-drawer/entity-detail-layout.js';
import {
  DetailFieldGrid,
  EmptyTabState,
  PropertySection,
} from '../entity-drawer/entity-drawer-sections.js';
import { EntityAvatar, EntityDrawerHeader } from '../entity-drawer/entity-drawer-shell.js';
import {
  type EvidenceRowItem,
  ExpandableEvidenceList,
} from '../entity-drawer/expandable-evidence-list.js';
import { getProjectTypeIcon } from '../entity-drawer/project-type-icon.js';
import { SignalFeed } from '../entity-drawer/signal-feed.js';
import { SignalScopeToggle } from '../entity-drawer/signal-scope-toggle.js';
import { ReportDataIssueDialog, ReportIssueButton } from '../feedback/report-data-issue-dialog.js';
import { ProjectSourceAvatar } from '../projects/project-source-avatar.js';
import { StatusBadge } from '../projects/status-badge.js';
import { WatchRecordButton } from '../watch/watch-record-button.js';
import { AccountContactsTab } from './account-contacts-tab.js';
import { AccountPortfolioSection } from './account-portfolio-section.js';
import {
  AccountAbout,
  AccountCompanySection,
  AccountSpecialtiesSection,
} from './account-profile.js';

interface AccountDetailProps extends RecordDetailChromeProps {
  accountId: string;
  seedAccount?: AccountRow | null;
  source: OpenSource;
}

export const ACCOUNT_DETAIL_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'signals', label: 'Signals' },
  { value: 'projects', label: 'Projects' },
  { value: 'contacts', label: 'Contacts' },
];

export function AccountDetail({
  accountId,
  seedAccount,
  source,
  mode,
  onToggleMode,
  backLabel,
  onBack,
  onPrev,
  onNext,
  onNavigate,
  initialTab,
}: AccountDetailProps) {
  const [reportOpen, setReportOpen] = React.useState(false);
  const { resolvedTheme } = useTheme();

  useCaptureOnce(accountId, (id) => capture('account_opened', { account_id: id, source }));

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id;
  const [signalScope, setSignalScope] = useSignalScope(accountId);
  const detailQuery = useAccountDetail(accountId, workspaceId);
  const projectsQuery = useAccountProjects(accountId, workspaceId);
  const signalsQuery = useAccountSignals(accountId, workspaceId, signalScope);
  const contactsQuery = useAccountContacts(accountId, workspaceId);
  const portfolioQuery = useAccountPortfolio(accountId, workspaceId);

  const apiAccount = detailQuery.data?.account ?? null;
  const linkedinPage = normalizeExternalUrl(apiAccount?.linkedin_url ?? null) ?? undefined;
  const realProjects: ApiLinkedProject[] = projectsQuery.data?.projects ?? [];
  const realSignals = signalsQuery.data?.signals ?? [];
  const realContacts: ApiContactSummary[] = contactsQuery.data?.contacts ?? [];
  const contactsCount = contactsQuery.data ? realContacts.length : undefined;
  // A query waiting on the workspace id is still loading, not empty.
  const pending = (query: { isLoading: boolean }) => workspaceQuery.isLoading || query.isLoading;
  const detailPending = !apiAccount && pending(detailQuery);
  // Profile, counts and portfolio land together, so the overview fills in once.
  const overviewPending = detailPending || pending(contactsQuery) || pending(portfolioQuery);
  const displayAccount = React.useMemo(
    () => accountDisplayRow(seedAccount, apiAccount, contactsCount),
    [seedAccount, apiAccount, contactsCount],
  );
  const displayWebsite = displayAccount ? accountWebsite(displayAccount) : null;
  // Same domain the logo.dev icon is keyed on (protocol + www stripped).
  const displayDomain = logoDomain(displayWebsite);
  const websiteLink = normalizeExternalUrl(displayWebsite) ?? undefined;
  const websiteLabel = displayWebsite ? `Open ${displayWebsite}` : undefined;

  const projectRows = React.useMemo<EvidenceRowItem[]>(() => {
    if (!displayAccount) return [];
    return realProjects.map((p) => ({
      key: p.id,
      entityKind: 'account',
      entityId: accountId,
      projectId: p.id,
      entityName: displayAccount.name,
      avatar: (
        <ProjectSourceAvatar
          name={p.name ?? p.primary_address}
          latestSource={p.latest_source}
          fallbackIcon={getProjectTypeIcon('other')}
          size="sm"
        />
      ),
      title: p.name ?? p.primary_address,
      subtitle: `${p.city}, ${p.state}`,
      meta: <ApiStageBadge stage={p.stage} />,
      openLabel: 'Open project',
      openIcon: Tag,
      onOpen: () =>
        onNavigate?.(
          { type: 'project', id: p.id },
          { seed: apiProjectToSeedRow(p), fromLabel: displayAccount.name },
        ),
    }));
  }, [accountId, displayAccount, onNavigate, realProjects]);

  const columns = mode === 'expanded' ? 1 : 2;
  const summarySkeleton = (
    <>
      <SectionSkeleton rows={6} columns={columns} />
      <SectionSkeleton rows={3} columns={columns} />
      <SectionSkeleton rows={4} columns={columns} />
    </>
  );

  if (!displayAccount) {
    // Header keeps close, back, and prev/next reachable while loading or after a failed load.
    const header = (
      <EntityDrawerHeader
        eyebrow="Account details"
        title={detailQuery.isError ? 'Unable to load account' : 'Loading account…'}
        metaIcon={Building2}
        metaText=""
        entityIcon={<EntityAvatar alt="" fallbackIcon={Building2} />}
        onPrev={onPrev}
        onNext={onNext}
        mode={mode}
        onToggleMode={onToggleMode}
        backLabel={backLabel}
        onBack={onBack}
        loading={!detailQuery.isError}
      />
    );
    if (detailQuery.isError) return header;
    return (
      <EntityDetailLayout
        mode={mode}
        initialTab={initialTab}
        header={header}
        summary={
          <>
            {mode === 'expanded' ? <TextSkeleton /> : null}
            {summarySkeleton}
          </>
        }
        tabs={ACCOUNT_DETAIL_TABS.map((tab) => ({
          ...tab,
          drawerOnly: tab.value === 'overview',
          content: tab.value === 'overview' ? <TextSkeleton /> : <ListSkeleton />,
        }))}
      />
    );
  }

  const signalsContent = (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Signals</h3>
        <SignalScopeToggle scope={signalScope} onScopeChange={setSignalScope} />
      </div>

      <SignalFeed
        signals={realSignals}
        isLoading={pending(signalsQuery)}
        withheld={signalsQuery.data?.withheld}
        workspaceSlug={workspaceSlug}
      />
    </div>
  );

  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;
  const portfolio = portfolioQuery.data?.portfolio;
  const summarySections = overviewPending ? (
    summarySkeleton
  ) : (
    <>
      <PropertySection title="Details">
        <DetailFieldGrid
          columns={columns}
          fields={[
            { label: 'Projects', value: displayAccount.projectsCount.toLocaleString() },
            {
              label: 'Contacts',
              value:
                contactsCount == null
                  ? displayAccount.contactsCount.toLocaleString()
                  : contactsCountShown(contactsCount),
            },
            {
              label: 'Last activity',
              value: formatSignalCalendarDate(displayAccount.lastActivityAt),
            },
            {
              label: 'Created',
              value: new Date(displayAccount.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }),
            },
          ]}
        />
      </PropertySection>
      {apiAccount ? (
        <AccountCompanySection
          account={apiAccount}
          mapboxToken={mapboxToken}
          theme={resolvedTheme}
          columns={columns}
        />
      ) : null}
      {apiAccount ? <AccountSpecialtiesSection account={apiAccount} /> : null}
      {portfolio ? <AccountPortfolioSection portfolio={portfolio} columns={columns} /> : null}
    </>
  );

  const overviewContent = overviewPending ? (
    <TextSkeleton />
  ) : apiAccount?.description ? (
    <AccountAbout account={apiAccount} />
  ) : null;

  // Expanded drops the Overview tab, so the description leads the rail instead.
  const summary =
    mode === 'expanded' ? (
      <>
        {overviewContent}
        {summarySections}
      </>
    ) : (
      summarySections
    );

  return (
    <>
      <ReportDataIssueDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        workspaceId={workspaceId}
        targetType="account"
        targetId={accountId}
        targetName={displayAccount.name}
        buildContext={() =>
          buildReportContext({
            route: pathname,
            account: { id: accountId, name: displayAccount.name },
            projects: realProjects,
            contacts: realContacts,
            signals: realSignals,
          })
        }
      />
      <EntityDetailLayout
        mode={mode}
        initialTab={initialTab}
        header={
          <EntityDrawerHeader
            eyebrow="Account details"
            title={displayAccount.name}
            metaIcon={Building2}
            metaText={displayDomain ?? `${displayAccount.city}, ${displayAccount.state}`}
            metaSlot={
              displayDomain ? (
                <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <a
                    href={normalizeExternalUrl(displayWebsite)}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate hover:text-foreground hover:underline"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {displayDomain}
                  </a>
                  <CopyButton value={displayDomain} label="Copy domain" />
                </div>
              ) : undefined
            }
            entityIcon={
              <EntityAvatar
                src={logoUrl(displayWebsite)}
                alt={displayAccount.name}
                fallbackIcon={Building2}
                href={normalizeExternalUrl(displayWebsite)}
              />
            }
            leadingAction={
              <WatchRecordButton workspaceId={workspaceId} dataset="account" recordId={accountId} />
            }
            action={<ReportIssueButton onClick={() => setReportOpen(true)} />}
            // LinkedIn first, then the site, then the report button.
            externalUrl={linkedinPage ?? websiteLink}
            externalLabel={linkedinPage ? 'Open LinkedIn page' : websiteLabel}
            externalIcon={
              linkedinPage ? <AppLogo name="linkedin" size={16} title="LinkedIn" /> : undefined
            }
            moreExternalLinks={
              linkedinPage && websiteLink
                ? [
                    {
                      url: websiteLink,
                      label: websiteLabel ?? 'Open website',
                      icon: <LinkIcon className="h-4 w-4" />,
                    },
                  ]
                : undefined
            }
            onPrev={onPrev}
            onNext={onNext}
            mode={mode}
            onToggleMode={onToggleMode}
            backLabel={backLabel}
            onBack={onBack}
          />
        }
        summary={summary}
        tabs={[
          { value: 'overview', label: 'Overview', content: overviewContent, drawerOnly: true },
          { value: 'signals', label: 'Signals', content: signalsContent },
          {
            value: 'projects',
            label: 'Projects',
            count: displayAccount.projectsCount,
            content: pending(projectsQuery) ? (
              <ListSkeleton />
            ) : projectRows.length ? (
              <ExpandableEvidenceList workspaceId={workspaceId} rows={projectRows} />
            ) : (
              <EmptyTabState message="No related projects" />
            ),
          },
          {
            value: 'contacts',
            label: 'Contacts',
            count: contactsCount,
            countLabel: contactsCount == null ? undefined : contactsCountShown(contactsCount),
            content: !workspaceId ? (
              workspaceQuery.isLoading ? (
                <ListSkeleton />
              ) : (
                <EmptyTabState message="Unable to load contacts" />
              )
            ) : (
              <EnrichScopeProvider workspaceId={workspaceId}>
                <AccountContactsTab
                  accountId={accountId}
                  accountName={displayAccount.name}
                  accountWebsite={displayWebsite}
                  onOpenContact={(contactId) =>
                    onNavigate?.(
                      { type: 'contact', id: contactId },
                      { fromLabel: displayAccount.name },
                    )
                  }
                />
              </EnrichScopeProvider>
            ),
          },
        ]}
      />
    </>
  );
}

// Stage vocabulary is now canonical on both the API and web; the crosswalk is identity.
const API_STAGE_TO_ROW: Record<NonNullable<ApiLinkedProject['stage']>, ProjectRow['stage']> = {
  pre_filing: 'pre_filing',
  filed: 'filed',
  under_review: 'under_review',
  approved: 'approved',
  permitted: 'permitted',
  under_construction: 'under_construction',
  completed: 'completed',
};

// Header seed for the navigated project; the detail query fills in the rest.
export function apiProjectToSeedRow(p: ApiLinkedProject): ProjectRow {
  return {
    id: p.id,
    name: p.name ?? p.primary_address,
    address: p.primary_address,
    city: p.city,
    state: p.state,
    type: UNKNOWN_PROJECT_TYPE,
    sqft: null,
    stage: p.stage ? API_STAGE_TO_ROW[p.stage] : null,
    disposition: p.disposition,
    developer: '—',
    architect: '—',
    detectedAt: p.last_signal_at ?? new Date().toISOString(),
    source: p.latest_source?.display_name ?? '—',
    sourceBaseUrl: p.latest_source?.base_url ?? null,
    sourceLogoDomain: p.latest_source?.logo_domain ?? null,
    lat: Number.NaN,
    lng: Number.NaN,
    scores: {},
  };
}

export function accountDisplayRow(
  seed: AccountRow | null | undefined,
  detail: ApiAccountDetail | null | undefined,
  contactsCount?: number,
): AccountRow | null {
  const row = detail
    ? { ...apiAccountToRow(detail), contactsCount: seed?.contactsCount ?? 0 }
    : (seed ?? null);
  if (!row) return null;
  return contactsCount == null ? row : { ...row, contactsCount };
}

function ApiStageBadge({ stage }: { stage: ApiLinkedProject['stage'] }) {
  return <StatusBadge {...stageMeta(stage)} />;
}
